import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

/**
 * Hook to fetch product option groups and their values
 */
export const useProductOptions = (productId) => {
  const [optionGroups, setOptionGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productId) {
      setOptionGroups([]);
      setLoading(false);
      return;
    }

    const fetchOptions = async () => {
      try {
        setLoading(true);
        console.log('Fetching product options for product:', productId);

        // Fetch option groups with their values
        const { data: groupsData, error: groupsError } = await supabase
          .from('product_option_groups')
          .select(`
            *,
            values:product_option_values(
              id,
              value,
              meta,
              position
            )
          `)
          .eq('product_id', productId)
          .order('position', { ascending: true });

        if (groupsError) {
          throw groupsError;
        }

        // Sort values within each group by position
        const groupsWithSortedValues = (groupsData || []).map(group => ({
          ...group,
          values: (group.values || []).sort((a, b) => (a.position || 0) - (b.position || 0))
        }));

        console.log('Product options fetched:', groupsWithSortedValues);
        setOptionGroups(groupsWithSortedValues);
      } catch (err) {
        console.error('Error fetching product options:', err);
        setError(err.message);
        setOptionGroups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [productId]);

  return { optionGroups, loading, error };
};

/**
 * Hook to fetch product variants and their option mappings
 */
export const useProductVariants = (productId) => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productId) {
      setVariants([]);
      setLoading(false);
      return;
    }

    const fetchVariants = async () => {
      try {
        setLoading(true);
        console.log('Fetching product variants for product:', productId);

        // Fetch variants with their option mappings
        const { data: variantsData, error: variantsError } = await supabase
          .from('product_variants')
          .select(`
            *,
            variant_options:product_variant_options(
              group_id,
              value_id,
              option_group:product_option_groups(
                id,
                name,
                label
              ),
              option_value:product_option_values(
                id,
                value
              )
            )
          `)
          .eq('product_id', productId)
          .eq('status', 'active')
          .order('created_at', { ascending: true });

        if (variantsError) {
          throw variantsError;
        }

        // Normalize numeric fields
        const normalizedVariants = (variantsData || []).map(variant => ({
          ...variant,
          price: variant.price != null ? Number(variant.price) : 0,
          stock: variant.stock != null ? Number(variant.stock) : 0,
        }));

        console.log('Product variants fetched:', normalizedVariants);
        setVariants(normalizedVariants);
      } catch (err) {
        console.error('Error fetching product variants:', err);
        setError(err.message);
        setVariants([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVariants();
  }, [productId]);

  return { variants, loading, error };
};

/**
 * Helper function to find variant by selected option values
 */
export const findVariantByOptions = (variants, selectedOptions) => {
  if (!variants || variants.length === 0 || !selectedOptions) {
    return null;
  }

  // Convert selectedOptions from { groupId: valueId } to a comparable format
  const selectedEntries = Object.entries(selectedOptions).filter(([_, valueId]) => valueId != null);
  
  if (selectedEntries.length === 0) {
    return null;
  }

  // Find variant that matches all selected options
  return variants.find(variant => {
    if (!variant.variant_options || variant.variant_options.length === 0) {
      return false;
    }

    // Check if variant has all selected option mappings
    const variantOptionMap = {};
    variant.variant_options.forEach(vopt => {
      variantOptionMap[vopt.group_id] = vopt.value_id;
    });

    // All selected options must match variant's options
    return selectedEntries.every(([groupId, valueId]) => 
      variantOptionMap[groupId] === valueId
    ) && Object.keys(variantOptionMap).length === selectedEntries.length;
  });
};

